import { apiFetch, apiFetchRaw, latestOnly } from './api-client';

/**
 * Raw shape returned by GET /history/... - `details` is a JSON-encoded string (e.g.
 * containing filename/relationName/borderoId/fullies/empties for FAVOR sends), already
 * Base64-decoded by `searchFavors` below - see `decodeHistoryItemDetails`.
 */
export interface HistoryItem {
  id: number;
  operationType: string;
  objectId: number;
  details: string;
  status: string;
  timestamp: number;
}

/**
 * `HistoryItem.details` is Base64-encoded on the wire by the v3 REST API's own Jackson
 * serialization (`HistoryItemMixin.java`: `@JsonSerialize(using = Base64Serializer.class)`,
 * applied to every v3 endpoint returning a HistoryItem, not something new to /history/favors) -
 * decode it back to the raw JSON text here so callers can `JSON.parse()` it directly, the
 * same way the server side does via Gson before that encoding is ever applied.
 */
function decodeHistoryItemDetails(base64: string): string {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

export interface SearchFavorsParams {
  since: number;
  until: number;
  search?: string;
  offset?: number;
  limit?: number;
  getUsers?: boolean;
  // Only 'timestamp' is backed by a real, indexed column - the rest of the row's fields
  // live inside the details JSON/XML blob, so the API only supports ordering by this today.
  orderBy?: 'timestamp';
  orderByDirection?: 'asc' | 'desc';
}

export interface SearchFavorsResult {
  items: HistoryItem[];
  hasMore: boolean;
}

/**
 * Search sent FAVOR (Bordero) EDI messages via GET /history/favors (Redmine #23370).
 * `hasMore` is read from the response header, not the item count - the API's post-fetch
 * ACL filtering can shrink the returned page independently of whether more pages exist.
 *
 * Wrapped in `latestOnly` - a caller retriggering this quickly (e.g. clicking a sort
 * column twice) always supersedes its own previous call, so the earlier in-flight
 * request is aborted rather than left to complete and race the newer one.
 */
export const searchFavors = latestOnly(async (signal: AbortSignal, params: SearchFavorsParams): Promise<SearchFavorsResult> => {
  const query = new URLSearchParams();
  query.set('since', String(params.since));
  query.set('until', String(params.until));
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.offset !== undefined) {
    query.set('offset', String(params.offset));
  }
  if (params.limit !== undefined) {
    query.set('limit', String(params.limit));
  }
  if (params.getUsers !== undefined) {
    query.set('getUsers', String(params.getUsers));
  }
  if (params.orderBy) {
    query.set('orderBy', params.orderBy);
  }
  if (params.orderByDirection) {
    query.set('orderByDirection', params.orderByDirection);
  }

  const { data, headers } = await apiFetch<HistoryItem[]>(`history/favors?${query.toString()}`, { signal });
  return {
    items: data.map((item) => ({ ...item, details: decodeHistoryItemDetails(item.details) })),
    hasMore: headers.get('hasMore') === 'true',
  };
});

const CONTENT_DISPOSITION_FILENAME_REGEX = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;

/**
 * Downloads the stored content for a single history item (e.g. a sent FAVOR/Bordero EDI
 * message) via GET /history/{operationId}/content, using its persisted filename.
 * This is a deliberate TS/fetch port of request-manager.js's downloadFile() technique
 * (same Content-Disposition filename-parsing regex) - not a new invention.
 */
export async function downloadHistoryContent(operationId: number): Promise<void> {
  const response = await apiFetchRaw(`history/${operationId}/content`);

  let filename = `history-${operationId}.dat`;
  const disposition = response.headers.get('Content-Disposition');
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const match = CONTENT_DISPOSITION_FILENAME_REGEX.exec(disposition);
    if (match?.[1]) {
      filename = match[1].replace(/['"]/g, '');
    }
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
